import { createContext, useContext, useState, useCallback, useMemo } from "react";

/**
 * Contexto global do player de áudio da Rádio JurisTube.
 *
 * Existe UM único iframe do Spotify renderizado pelo ``StickyRadioPlayer``
 * fixado no rodapé da aplicação. Quando o usuário clica em "Tocar
 * canal" ou "Tocar episódio" em qualquer página, este contexto guarda
 * a referência (kind + spotifyId) e o sticky player atualiza seu
 * ``src`` — preservando o estado de reprodução enquanto o usuário
 * navega por outras áreas do site.
 *
 * Estados possíveis de ``current``:
 *   * ``null``  — nenhum áudio tocando; sticky player oculto
 *   * ``{ kind: "show",    spotifyId, channelSlug, channelName }``
 *   * ``{ kind: "episode", spotifyId, channelSlug, channelName, episodeSlug, episodeTitle }``
 */
const RadioPlayerContext = createContext({
  current: null,
  playShow: () => {},
  playEpisode: () => {},
  stop: () => {},
});

export function RadioPlayerProvider({ children }) {
  const [current, setCurrent] = useState(null);

  const playShow = useCallback((opts) => {
    if (!opts?.spotifyId) return;
    setCurrent({ kind: "show", ...opts });
  }, []);

  const playEpisode = useCallback((opts) => {
    if (!opts?.spotifyId) return;
    setCurrent({ kind: "episode", ...opts });
  }, []);

  // Iter142 — seekTo: reposiciona o player para um instante específico
  // do episódio atual. Tecnicamente, o iframe do Spotify embed aceita
  // ``?t=<segundos>`` no src; trocamos o ``seekTime`` no estado para
  // que o ``StickyRadioPlayer`` recomponha a URL. Custo: o iframe é
  // recarregado (inevitável sem postMessage estável da Spotify).
  // ``nonce`` força um novo render mesmo quando o usuário clica
  // duas vezes no mesmo timestamp.
  const seekTo = useCallback((seconds) => {
    setCurrent((c) => {
      if (!c) return c;
      return {
        ...c,
        seekTime: Math.max(0, Math.floor(Number(seconds) || 0)),
        seekNonce: Date.now(),
      };
    });
  }, []);

  const stop = useCallback(() => setCurrent(null), []);

  const value = useMemo(
    () => ({ current, playShow, playEpisode, seekTo, stop }),
    [current, playShow, playEpisode, seekTo, stop],
  );

  return (
    <RadioPlayerContext.Provider value={value}>
      {children}
    </RadioPlayerContext.Provider>
  );
}

export function useRadioPlayer() {
  return useContext(RadioPlayerContext);
}

/** Helper que monta a URL do embed do Spotify.
 * ``seekTime`` (segundos) opcional — adiciona ``&t=<seconds>`` que o
 * Spotify reconhece para começar a reprodução naquele instante.
 */
export function spotifyEmbedUrl(kind, id, seekTime) {
  if (!id) return "";
  const path = kind === "show" ? "show" : "episode";
  const base = `https://open.spotify.com/embed/${path}/${id}?utm_source=generator`;
  if (typeof seekTime === "number" && seekTime > 0) {
    return `${base}&t=${Math.floor(seekTime)}`;
  }
  return base;
}
